import { describeError } from "@/api/client";
import type { Workspace } from "@/api/session";
import { useUpdateWorkspace } from "@/api/workspace";
import { Link } from "react-router";
import { Switch, useToast } from "@/components";
import { SCHEDULE_SWITCHES } from "../api";
import styles from "../settings.module.css";

/** Turn recurring worker jobs on or off (settings.schedules); each switch saves immediately. */
export function SchedulesSection({ workspace, canEdit }: { workspace: Workspace; canEdit: boolean }) {
  const update = useUpdateWorkspace();
  const toast = useToast();
  const stored = workspace.settings.schedules ?? {};
  const pending = update.isPending ? update.variables?.settings?.schedules : undefined;

  function toggle(key: string, label: string, enabled: boolean) {
    update.mutate(
      { settings: { schedules: { [key]: enabled } } },
      {
        onSuccess: () => toast.success(`${label} turned ${enabled ? "on" : "off"}`),
        onError: (error) => toast.error(`Couldn’t change “${label}”`, { description: describeError(error) }),
      },
    );
  }

  return (
    <div className={styles.panel}>
      <p className={styles.hint}>
        The worker runs these jobs on a schedule in the server’s time zone. Switching one off skips its runs until you
        switch it back on; you can still start any job by hand from <Link to="/jobs">Jobs</Link>.
      </p>
      <div className={styles.switchList}>
        {SCHEDULE_SWITCHES.map((schedule) => {
          const pendingValue =
            pending && schedule.key in pending
              ? Boolean((pending as Record<string, unknown>)[schedule.key])
              : undefined;
          const checked = pendingValue ?? stored[schedule.key] ?? true;
          return (
            <div key={schedule.key} className={styles.switchRow}>
              <Switch
                label={schedule.label}
                description={
                  <>
                    <span className={styles.switchWhen}>{schedule.when}.</span> {schedule.description}
                  </>
                }
                checked={checked}
                pending={pendingValue !== undefined}
                disabled={!canEdit || (update.isPending && pendingValue === undefined)}
                onCheckedChange={(enabled) => toggle(schedule.key, schedule.label, enabled)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
